import {
  BapiPrice,
  CentAmount,
  ProductImage,
  WishlistItem,
} from '@aboutyou/backbone';
import { v4 as uuid } from 'uuid';
import { getImgUrl } from 'modules/media';
import { OrderItem } from 'commerce/scayle/auth/types';
import { BapiProduct, CartItem } from 'commerce/scayle/user-interaction/types';
import {
  IAdaptedBasicProduct,
  IAdaptedListProduct,
  IAdaptedProduct,
  IProductVariant,
} from './types';

const generateBullets = (shortDescription?: string) => {
  if (!shortDescription) {
    return [];
  }

  // Separating string into an array of bullets, where each new point is marked as 1) point1\n2) point2...etc.
  const regex = /\d+\)\s*(.*?)(?=\n|n\d+\)|$)/g;
  const matches = shortDescription.match(regex);

  // clear points from marks
  return matches
    ? matches.map((match) => match.replace(/^\d+\)\s*/, '').trim())
    : [];
};

const mapAssets = (assets: ProductImage[]) => {
  return assets.map((c) => {
    return { hash: c.hash, id: uuid() };
  });
};

export class Product {
  private product: BapiProduct;

  /**
   *
   * @param product BapiProduct
   * @returns - instance of Product
   *
   */
  static createProduct(product: BapiProduct) {
    return new Product(product);
  }

  static createCartProduct(item: CartItem) {
    return new Product({
      ...item.product,
      variants: [item.variant],
    });
  }

  static createOrderProduct(item: OrderItem) {
    const product = item.product as BapiProduct;
    const variants = [
      { ...item.variant, price: item.price as unknown as BapiPrice },
    ] as BapiProduct['variants'];

    return new Product({
      ...product,
      variants,
    });
  }

  static createWishlistProduct(item: WishlistItem) {
    const product = item.product as BapiProduct;
    const variants = [item.variant] as BapiProduct['variants'];
    return new Product({
      ...product,
      variants,
    });
  }

  constructor(product: BapiProduct) {
    this.product = product;
  }

  private getAttributeValue(attributeName: string) {
    const attribute = this.product.attributes?.[attributeName];
    const [first] = [attribute?.values].flat();

    return first?.label;
  }

  private getAttributeValues(attributeName: string): string[] | undefined {
    const attribute = this.product.attributes?.[attributeName];
    const values = attribute?.values;

    return Array.isArray(values)
      ? values.map((value) => value.label)
      : values?.label.split(',');
  }

  private getAdvancedAttributeValue(
    attributeName: string,
    fromVariant = false,
  ) {
    const product = fromVariant ? this.product.variants?.[0] : this.product;
    const advancedAttribute = product?.advancedAttributes?.[attributeName];

    if (advancedAttribute) {
      return advancedAttribute?.values?.[0]?.fieldSet?.[0]?.[0]?.value;
    }
    return null;
  }

  private getAdvancedAttribute(attributeName: string) {
    const advancedAttribute = this.product.advancedAttributes?.[attributeName];

    return {
      value:
        advancedAttribute?.values?.[0]?.fieldSet?.[0]?.[0]?.value?.toString(),
      label: advancedAttribute?.label?.toString(),
    };
  }

  static getMaximumQuantityPerOrder(variant?: IProductVariant) {
    const [first] = [
      variant?.attributes?.maximumQuantityPerOrder?.values,
    ].flat();

    return Number(first?.label);
  }

  get name() {
    return this.getAdvancedAttributeValue(
      'masterProperties.headline',
    ) as string;
  }

  get altName() {
    return this.name
      ?.toLowerCase() // convert the entire string to lowercase
      .replace(/\b(\d+),(\d+)\b/g, '$1 $2') // change comma in odd numbers to space
      .replace(/['",]/g, '') // remove quotes and commas
      .replace(/\s*,\s*|\s+|\[s\]*/g, '-'); // change [s], spaces to -
  }

  get category() {
    const masterCategories = this.getAttributeValues('category');
    if (masterCategories) {
      const lastCategory = masterCategories.slice(-1)[0];
      return lastCategory?.split('|').pop();
    }
  }

  // get all product categories
  get categories() {
    const categories = this.product.categories;

    return categories?.reduce(
      (acc, item) => {
        const lastItem = item[item.length - 1];
        acc[lastItem.categoryId] = item;

        return acc;
      },
      {} as Record<
        number,
        { categoryId: number; categoryName: string; categoryUrl: string }[]
      >,
    );
  }

  get masterCategory() {
    return this.product.baseCategories?.[0]?.categoryName;
  }

  get label() {
    return this.getAttributeValue('productProperties.productLabel');
  }

  get tags() {
    return this.getAttributeValues('masterProperties.marketingTags');
  }

  get price() {
    return this.product?.variants?.[0].price as BapiPrice & {
      recommendedRetailPrice: CentAmount;
    };
  }

  get id() {
    return Number(this.product?.variants?.[0].id);
  }

  get referenceKey() {
    return String(this.product.referenceKey);
  }

  get longDesc() {
    return this.getAdvancedAttributeValue(
      'masterProperties.longDescription',
    ) as string;
  }

  get enrichedLongDesc() {
    return this.getAdvancedAttributeValue(
      'masterProperties.longDescriptionEnriched',
    ) as string;
  }

  get shortDesc() {
    return this.getAdvancedAttributeValue(
      'masterProperties.shortDescription',
    ) as string;
  }

  get bullets() {
    return generateBullets(this.shortDesc);
  }

  get color() {
    const { values, label } =
      this.product?.attributes?.['productProperties.color'] || {};
    const [color] = [values].flat();

    return {
      label,
      value: color?.label,
    };
  }

  get colors() {
    const colors =
      this.product?.attributes?.['productProperties.color']?.values;

    return Array.isArray(colors)
      ? colors
          .map(({ label, value }) => ({
            label,
            value,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      : [];
  }

  get images() {
    // images field can be not only image, but pdf
    const images = (this.product.images || []).filter((doc) =>
      ['png', 'jpg'].some((ext) => doc.hash.endsWith(ext)),
    );

    return mapAssets(images);
  }

  get pdfs() {
    const pdfs = (this.product.images || []).filter((doc) => {
      const [first] = [doc.attributes?.['image.imageType']?.values].flat();

      if (first?.label === 'pdf') {
        return first;
      }
    });

    return mapAssets(pdfs);
  }

  get pdfUrl() {
    return this.getAdvancedAttributeValue('masterProperties.manual') as string;
  }

  get minimumOrderValue() {
    return this.getAdvancedAttributeValue('price.minimumOrderValue', true);
  }

  get minimumOrderValueSummable() {
    return this.getAdvancedAttributeValue(
      'price.minimumOrderValueSummable',
      true,
    );
  }

  get siblings() {
    return (
      this.product.siblings?.map((p) =>
        Product.createProduct(p).getBasicFields(),
      ) || null
    );
  }

  get variants() {
    return this.product.variants as any as IProductVariant[];
  }

  get seoUrl(): string {
    return String(this.getAdvancedAttributeValue('seo.url'));
  }

  get href() {
    return `/p/${this.seoUrl}`;
  }

  get coverImage() {
    const [firstImage] = this.images;

    return firstImage?.hash && getImgUrl(firstImage?.hash);
  }

  get limitForCart() {
    return Product.getMaximumQuantityPerOrder(
      this.product.variants?.[0] as any as IProductVariant,
    );
  }

  get brand() {
    return this.getAttributeValue('brand');
  }

  get video() {
    const brand = this.getAdvancedAttributeValue(
      'productProperties.brandVideoShortUrl',
    ) as string;
    const closeUp = this.getAdvancedAttributeValue(
      'productProperties.closeUpVideoShortUrl',
    ) as string;
    const model = this.getAdvancedAttributeValue(
      'productProperties.modelVideoShortUrl',
    ) as string;

    return [brand, model, closeUp].filter(Boolean);
  }

  get isActive() {
    return this.product?.isActive;
  }

  get isSoldOut() {
    return this.product?.isSoldOut;
  }

  get size() {
    return this.variants?.length
      ? this.variants.map((variant) => {
          const values = variant?.attributes?.size?.values;

          return {
            // ...variant?.attributes?.size?.values,
            label: values?.label,
            id: values?.id,
            value: values?.value,
            referenceKey: variant.referenceKey,
            price: variant.price,
            stock: variant.stock.quantity,
          };
        })
      : [];
  }

  get masterKey() {
    return this.product.masterKey;
  }

  get measurementUnit() {
    return this.getAttributeValue('contents.contentMeasuringUnit');
  }

  get value() {
    return this.getAdvancedAttributeValue('contents.contentValue');
  }

  get isMeasurableProduct() {
    const unit = this.getAttributeValue('contents.contentMeasuringUnit') || '';
    return ['ml', 'g', 'Stk'].includes(unit);
  }

  get relatedMasters() {
    return this.getAttributeValues('cmmd.relatedMaster');
  }

  get discountPercentage() {
    return Math.floor(
      ((this.price?.recommendedRetailPrice - this.price?.withTax) /
        this.price?.recommendedRetailPrice) *
        100,
    );
  }

  get specifications() {
    return {
      weight: this.weight,
      materials: this.materials,
      color: this.color,
      battery: this.battery,
      length: this.length,
      diameter: this.diameter,
      shaft: this.shaft,
      width: this.width,
      height: this.height,
      content: this.content,
    };
  }

  get weight() {
    return this.getAdvancedAttribute('cmmd.productWeightNet');
  }

  get battery() {
    return this.getAdvancedAttribute('batterySpecifications.batterySummary');
  }

  get length() {
    return this.getAdvancedAttribute('dimension.maxLengthInMm');
  }

  get diameter() {
    return this.getAdvancedAttribute('dimension.maxDiameterInMm');
  }

  get shaft() {
    return this.getAdvancedAttribute('dimension.maxShaftLengthInMm');
  }

  get width() {
    return this.getAdvancedAttribute('dimension.maxWidthInMm');
  }

  get content() {
    const content = this.getAdvancedAttribute('contents.contentValue');
    const contentMeasuringUnit = this.getAttributeValue(
      'contents.contentMeasuringUnit',
    );

    const contentValue =
      content != null && contentMeasuringUnit != null
        ? `${content.value} ${contentMeasuringUnit}`
        : '';

    return {
      label: content?.label,
      value: contentValue,
    };
  }

  get height() {
    return this.getAdvancedAttribute('dimension.minHeightInMm');
  }

  get taxRatePercent() {
    return this.price?.tax?.vat.rate * 100;
  }

  get materials() {
    const material = this.product?.attributes?.['cmmd.material'];

    const materialValues = Array.isArray(material?.values)
      ? material.values
          .filter((value) => !!value.label)
          .map((value) => value.label)
          .join(', ')
      : material?.values.label;

    return {
      label: material?.label,
      value: materialValues,
    };
  }

  get composedMaterials() {
    const advancedAttribute =
      this.variants?.[0]?.advancedAttributes?.['cmv.composedMaterials'];

    return {
      value:
        advancedAttribute?.values?.[0]?.fieldSet?.[0]?.[0]?.value?.toString(),
      label: advancedAttribute?.label?.toString(),
    };
  }

  get seoTitle() {
    /*
     titleTagProduct - product level
     titleTag - variant level
   */
    return (this.getAdvancedAttributeValue('seo.titleTag') ||
      this.getAdvancedAttributeValue('seo.titleTagProduct')) as string;
  }

  get seoDescription() {
    /*
      metaDescriptionProduct - product level
      metaDescription - variant level
    */
    return (this.getAdvancedAttributeValue('seo.metaDescription') ||
      this.getAdvancedAttributeValue('seo.metaDescriptionProduct')) as string;
  }

  get seoRobotAttributes() {
    const getValue = (...args: string[]): boolean => {
      const attributeName = args.find(
        (name) => this.product?.attributes?.[name],
      ) as string;
      const attribute = this.product?.attributes?.[attributeName];
      const [value] = [attribute?.values].flat();

      return value?.value === 'true';
    };

    return {
      follow: getValue('seo.robotFollow', 'seoRobotFollow'),
      index: getValue('seo.robotIndex', 'seoRobotIndex'),
    };
  }

  get isPrimaryPage() {
    return this.product.isPrimaryPage;
  }

  getBasicFields(): IAdaptedBasicProduct {
    return {
      id: this.id,
      name: this.name,
      referenceKey: this.referenceKey,
      coverImage: this.coverImage,
      href: this.href,
      pdfUrl: this.pdfUrl,
      colors: this.colors,
      isActive: this.isActive,
      size: this.size,
      variants: this.variants,
      limitForCart: this.limitForCart,
      label: this.label,
      brand: this.brand,
      category: this.category,
      masterCategory: this.masterCategory,
      price: this.price,
      composedMaterials: this.composedMaterials,
      isSoldOut: this.isSoldOut,
      taxRatePercent: this.taxRatePercent,
      discountPercentage: this.discountPercentage,
      minimumOrderValue: Number(this.minimumOrderValue || 0),
      minimumOrderValueSummable: Number(this.minimumOrderValueSummable || 0),
    };
  }

  /**
   * mostly used for SSR,
   * generate plain object for transferring data server -> client
   */
  toJSON(): IAdaptedProduct {
    return {
      ...this.getBasicFields(),
      altName: this.altName,
      tags: this.tags,
      longDesc: this.longDesc,
      enrichedLongDesc: this.enrichedLongDesc,
      shortDesc: this.shortDesc,
      bullets: this.bullets,
      color: this.color,
      images: this.images,
      pdfUrl: this.pdfUrl,
      seoUrl: this.seoUrl,
      video: this.video,
      masterKey: this.masterKey,
      measurementUnit: this.measurementUnit,
      value: this.value,
      isMeasurableProduct: this.isMeasurableProduct,
      relatedMasters: this.relatedMasters,
      specifications: this.specifications,
      seoTitle: this.seoTitle,
      seoDescription: this.seoDescription,
      isPrimaryPage: this.isPrimaryPage,
      siblings: this.siblings,
      seoRobotAttributes: this.seoRobotAttributes,
    };
  }

  /**
   * used for Category SSR,
   * generate plain object for transferring data server -> client
   * used ProductList
   */
  toShortJSON(): IAdaptedListProduct {
    return {
      id: this.id,
      referenceKey: this.referenceKey,
      name: this.name,
      price: this.price,
      seoUrl: this.seoUrl,
      href: this.href,
      pdfUrl: this.pdfUrl,
      size: this.size,
      brand: this.brand,
      video: this.video,
      colors: this.colors,
      isActive: this.isActive,
      isSoldOut: this.isSoldOut,
      category: this.category,
      coverImage: this.coverImage,
      isPrimaryPage: this.isPrimaryPage,
      label: this.label,
      composedMaterials: this.composedMaterials,
      bullets: this.bullets,
      limitForCart: this.limitForCart,
      taxRatePercent: this.taxRatePercent,
      variants: this.variants,
      color: this.color,
      discountPercentage: this.discountPercentage,
      siblings: this.siblings,
    };
  }
}
